import React, { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Icon, IconArrowLeft } from '@flixbus/honeycomb-icons-react';
import { IconAttention } from '@flixbus/honeycomb-icons-react';
import { Button, Popup, PopupSection, Heading } from '@flixbus/honeycomb-react';

import { useVoucherFormContext } from '../Context';
import { generateMultipleVouchers, generateVoucher, saveComment } from '../../../apis';

import styles from './voucher-form-submit.module.scss';
import { useNotification } from '../../Notifications/Context';

const HIGH_VALUE_THRESHOLD = 300;
const HIGH_VALUE_CURRENCIES = new Set(['EUR', 'USD', 'GBP', 'CHF', 'CAD', 'AUD']);
const MONETARY_DISCOUNT_TYPES = new Set([
  'credit_amount',
  'value_marketing',
  'value_hybrid',
  'free_ride_hybrid',
]);

const getVoucherAmount = (discountType: {
  type?: string;
  credit?: number;
  discount?: number;
}): number => {
  if (discountType.type === 'value_marketing') return discountType.discount ?? 0;
  return discountType.credit ?? 0;
};

const VoucherFormSubmit = () => {
  const { pushToast } = useNotification();
  const {
    formState: { payload, activeDiscount, comment },
    formErrors,
  } = useVoucherFormContext();
  const navigate = useNavigate();
  const { count } = payload;
  const [submitting, setSubmitting] = useState(false);
  const [highValuePopupActive, setHighValuePopupActive] = useState(false);
  const confirmButtonRef = useRef<HTMLDivElement>(null);

  const handleSubmit = async () => {
    if (submitting) return;

    if (Object.keys(formErrors).length > 0) {
      pushToast({
        message: Object.values(formErrors)[0],
        status: 'warning',
      });
      return;
    }

    if (
      ['credit_amount', 'value_marketing', 'value_hybrid'].includes(activeDiscount) &&
      payload.discountType.currency &&
      payload.discountType.currency === 'MXN' &&
      !payload.entity
    ) {
      pushToast({
        message: 'Please select a contract!',
        status: 'warning',
      });
      return;
    }

    const amount = getVoucherAmount(payload.discountType);
    const currency = payload.discountType.currency || 'EUR';
    if (
      amount > HIGH_VALUE_THRESHOLD &&
      MONETARY_DISCOUNT_TYPES.has(payload.discountType.type || '') &&
      HIGH_VALUE_CURRENCIES.has(currency) &&
      !highValuePopupActive
    ) {
      setHighValuePopupActive(true);
      return;
    }
    setHighValuePopupActive(false);

    try {
      setSubmitting(true);
      const res =
        count === 1 ? await generateVoucher(payload) : await generateMultipleVouchers(payload);

      if (comment && comment.length > 0) {
        try {
          count === 1 && (await saveComment(res.id, { comment: comment }));
        } catch (e) {
          const message = e instanceof Error ? e.message : 'An unknown error occurred';

          pushToast({
            message,
            status: 'danger',
          });
        }
      }

      count === 1
        ? navigate(`/success/${res.id}`, { state: { voucher: res } })
        : navigate(`/batch/${res.id}`);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'An unknown error occurred';
      let detail = message;
      try {
        detail = JSON.parse(message)?.detail || message;
      } catch {
        // Backend returned a non-JSON (plain text) error body.
      }

      pushToast({
        message: detail,
        status: 'danger',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.container}>
      <Button Elem='a' appearance='link' RouterLink={Link} to='/'>
        <Icon InlineIcon={IconArrowLeft} />
        Cancel
      </Button>
      <div ref={confirmButtonRef} style={{ display: 'inline-block' }}>
        <Button
          size='md'
          appearance='primary'
          type='submit'
          loading={submitting}
          disabled={submitting}
          onClick={(e: React.MouseEvent<HTMLElement>) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          Confirm voucher creation
        </Button>
      </div>

      <Popup
        active={highValuePopupActive}
        onOverlayClick={() => setHighValuePopupActive(false)}
        targetRef={confirmButtonRef}
      >
        <PopupSection type='icon'>
          <Icon InlineIcon={IconAttention} size={12} style={{ fill: 'var(--hcr-warning-color)' }} />
        </PopupSection>
        <PopupSection type='title'>
          <Heading size={2} sectionHeader flushSpace>
            High value voucher
          </Heading>
        </PopupSection>
        <PopupSection type='content'>
          You are about to create a voucher worth{' '}
          <b>
            {new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: payload.discountType.currency || 'EUR',
              minimumFractionDigits: 2,
            }).format(getVoucherAmount(payload.discountType))}
          </b>
          . Please double-check the amount is correct before proceeding.
        </PopupSection>
        <PopupSection type='actions'>
          <Button onClick={() => setHighValuePopupActive(false)}>Go Back</Button>
          <Button
            appearance='primary'
            loading={submitting}
            disabled={submitting}
            onClick={(e: React.MouseEvent<HTMLElement>) => {
              e.preventDefault();
              handleSubmit();
            }}
          >
            Confirm
          </Button>
        </PopupSection>
      </Popup>
    </div>
  );
};

export default VoucherFormSubmit;
